import { oc } from "@orpc/contract";
import { z } from "zod";

/**
 * Scalar helpers
 */
const UUIDSchema = z.uuid();
const DecimalStringSchema = z.string().regex(/^-?\d+(\.\d+)?$/, {
  error: "Must be a numeric string",
});
const Decimal4dpSchema = z.string().regex(/^-?\d+(\.\d{1,4})?$/, {
  error: "Must be a number with at most 4 decimal places",
});

/** AS400 does not accept a VDM whose gross amount is $1,000,000 or more. */
export const AS400_VDM_GROSS_AMOUNT_LIMIT = 1_000_000;
export const AS400_VDM_GROSS_AMOUNT_LIMIT_MESSAGE =
  "AS400 does not accept VDMs of $1,000,000 or more. Split the claim across several VDMs.";

const VdmGrossAmountSchema = Decimal4dpSchema.refine(
  (value) => Number(value) < AS400_VDM_GROSS_AMOUNT_LIMIT,
  { error: AS400_VDM_GROSS_AMOUNT_LIMIT_MESSAGE },
);

/**
 * VDM Status enum (matching backend model)
 */
export const VDMStatusSchema = z.enum([
  "created",
  "as400_confirmed_create",
  "pending_as400_edit",
  "as400_confirmed_edit",
  "error_creating",
  "error_editing",
]);

/**
 * Display label for each VDM status — shared so the VDMs page badge and the
 * Excel export read the same.
 */
export const VDM_STATUS_LABELS: Record<
  z.infer<typeof VDMStatusSchema>,
  string
> = {
  created: "Created",
  as400_confirmed_create: "AS400 Confirmed",
  pending_as400_edit: "Awaiting AS400 Edit",
  as400_confirmed_edit: "AS400 Edit Confirmed",
  error_creating: "Error Creating",
  error_editing: "Error Editing",
};

/**
 * Pagination schema
 */
export const VDMPaginationSchema = z.object({
  total: z.int().nonnegative(),
  limit: z.int().positive(),
  offset: z.int().nonnegative(),
});

/**
 * Supplier summary for VDM response
 */
const VDMSupplierSchema = z.object({
  id: UUIDSchema,
  name: z.string(),
  companyId: UUIDSchema.nullable().optional(),
});

/**
 * VDM schema for RPC responses
 */
export const VDMSchema = z.object({
  id: UUIDSchema,
  organizationId: UUIDSchema,
  gentlyVdmNumber: z.int(),
  vdmNumber: z.int().nullable().optional(),
  grossAmount: DecimalStringSchema,
  status: VDMStatusSchema,
  calculateTax: z.boolean(),
  shippingProvince: z.string().nullable().optional(),
  specialInstructions1: z.string().nullable().optional(),
  specialInstructions2: z.string().nullable().optional(),
  specialInstructions3: z.string().nullable().optional(),
  commentLine1: z.string().nullable().optional(),
  commentLine2: z.string().nullable().optional(),
  emailToAddress: z.string().nullable().optional(),
  emailCcAddress: z.string().nullable().optional(),
  emailBccAddress: z.string().nullable().optional(),
  companyCode: z.string(),
  divisionCode: z.string().nullable().optional(),
  branchCode: z.string().nullable().optional(),
  gstHstAmount: DecimalStringSchema.nullable().optional(),
  qstPstAmount: DecimalStringSchema.nullable().optional(),
  totalTaxAmount: DecimalStringSchema.nullable().optional(),
  totalAmount: DecimalStringSchema.nullable().optional(),
  error: z.string().nullable().optional(),
  supplierId: UUIDSchema.nullable(),
  supplierVendorNumber: z.string(),
  supplier: VDMSupplierSchema.nullable().optional(),
  metadata: z.record(z.string(), z.unknown()).nullable().optional(),
  linkedSubmissionCount: z.int().nonnegative().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  deletedAt: z.coerce.date().nullable().optional(),
});

/**
 * List VDMs input schema with filters
 */
export const VDMSortBySchema = z.enum([
  "createdAt",
  "grossAmount",
  "totalAmount",
  "gentlyVdmNumber",
  "vdmNumber",
]);

export const ListVDMsInputSchema = z.object({
  // Filter by status
  status: z.array(VDMStatusSchema).optional(),
  // Filter by supplier
  supplierId: UUIDSchema.optional(),
  // Filter by company code
  companyCode: z.string().optional(),
  // Filter by division code
  divisionCode: z.string().optional(),
  // Filter by gently VDM number
  gentlyVdmNumber: z.int().optional(),
  // Filter by external VDM number
  vdmNumber: z.int().optional(),
  // Text search (matches VDM number or supplier name)
  search: z.string().optional(),
  // Date filters
  createdAtFrom: z.string().optional(),
  createdAtTo: z.string().optional(),
  // Sorting
  sortBy: VDMSortBySchema.optional(),
  sortOrder: z.enum(["ASC", "DESC"]).optional(),
  // Pagination
  limit: z.int().min(1).max(100).optional(),
  offset: z.int().min(0).optional(),
});

/**
 * List VDMs response schema
 */
export const ListVDMsResponseSchema = z.object({
  vdms: z.array(VDMSchema),
  pagination: VDMPaginationSchema,
});

/**
 * Download the filtered VDM list as an Excel workbook — one row per VDM,
 * mirroring the VDMs table. Takes the same filters as `list` (pagination
 * excluded: the export covers every match, up to a cap).
 */
export const VDMListFiltersSchema = ListVDMsInputSchema.omit({
  limit: true,
  offset: true,
});

export const DownloadVDMsExcelInputSchema = VDMListFiltersSchema;

export const DownloadVDMsExcelResponseSchema = z.object({
  /** base64-encoded .xlsx bytes */
  excel: z.string(),
  filename: z.string(),
  /** Rows written to the workbook. */
  count: z.int(),
  /** True when more VDMs matched than the export cap allows. */
  truncated: z.boolean(),
});

/**
 * Get single VDM input schema
 */
export const GetVDMInputSchema = z.object({
  id: UUIDSchema,
});

/**
 * Get VDM response schema
 */
export const GetVDMResponseSchema = VDMSchema;

/**
 * VDM Activity Type enum (matching backend model)
 */
export const VDMActivityTypeSchema = z.enum([
  "created",
  "sent_to_supplier",
  "supplier_replied",
  "reply_sent",
  "accepted",
  "rejected",
  "status_changed",
  "error_occurred",
  "resent",
  "manually_updated",
  "comment_added",
  "as400_responded",
  "submission_linked",
  "submission_unlinked",
]);

/**
 * User summary for activity response
 */
const ActivityUserSchema = z.object({
  id: UUIDSchema,
  name: z.string(),
});

/**
 * VDM Activity schema for RPC responses
 */
export const VDMActivitySchema = z.object({
  id: UUIDSchema,
  vdmId: UUIDSchema,
  activityType: VDMActivityTypeSchema,
  userId: UUIDSchema.nullable().optional(),
  user: ActivityUserSchema.nullable().optional(),
  inboundEmailId: UUIDSchema.nullable().optional(),
  eventAt: z.coerce.date(),
  description: z.string(),
  metadata: z.record(z.string(), z.unknown()).nullable().optional(),
  createdAt: z.coerce.date(),
});

/**
 * List VDM Activities input schema
 */
export const ListVDMActivitiesInputSchema = z.object({
  vdmId: UUIDSchema,
  // Pagination
  limit: z.int().min(1).max(100).optional(),
  offset: z.int().min(0).optional(),
});

/**
 * List VDM Activities response schema
 */
export const ListVDMActivitiesResponseSchema = z.object({
  activities: z.array(VDMActivitySchema),
  pagination: VDMPaginationSchema,
});

/**
 * Add Comment input schema
 */
export const AddVDMCommentInputSchema = z.object({
  vdmId: UUIDSchema,
  comment: z.string().min(1).max(2000),
});

/**
 * Add Comment response schema
 */
export const AddVDMCommentResponseSchema = VDMActivitySchema;

/**
 * Create VDM input schema
 */
export const CreateVDMInputSchema = z.object({
  companyCode: z.string().min(1),
  divisionCode: z.string(),
  branchCode: z.string(),
  calculateTax: z.boolean(),
  shippingProvince: z.string().min(1),
  grossAmount: VdmGrossAmountSchema,
  specialInstructions1: z.string().max(30).optional(),
  specialInstructions2: z.string().max(30).optional(),
  specialInstructions3: z.string().max(30).optional(),
  commentLine1: z.string().optional(),
  commentLine2: z.string().optional(),
  emailToAddress: z.string().email().optional().or(z.literal("")),
  emailCcAddress: z.string().max(70).optional().or(z.literal("")),
  emailBccAddress: z.string().max(70).optional().or(z.literal("")),
  supplierVendorNumber: z.string(),
  transactionIds: z.array(UUIDSchema).optional(),
});

/**
 * Create VDM response schema
 */
export const CreateVDMResponseSchema = VDMSchema;

/**
 * Batch VDM creation — create multiple VDMs at once (used by auto-VDM after batch-and-send).
 */
export const CreateBatchVDMsInputSchema = z.object({
  vdms: z.array(CreateVDMInputSchema).min(1).max(100),
});

const BatchVDMCreatedSchema = z.object({
  vdmId: UUIDSchema,
  gentlyVdmNumber: z.int(),
  supplierVendorNumber: z.string(),
  companyCode: z.string(),
  divisionCode: z.string(),
});

const BatchVDMFailedSchema = z.object({
  index: z.int(),
  reason: z.string(),
});

export const CreateBatchVDMsResponseSchema = z.object({
  created: z.array(BatchVDMCreatedSchema),
  failed: z.array(BatchVDMFailedSchema),
});

/**
 * Update VDM input schema (partial update, only editable fields)
 */
export const UpdateVDMInputSchema = z.object({
  id: UUIDSchema,
  supplierId: UUIDSchema.optional(),
  supplierVendorNumber: z.string().min(1).optional(),
  companyCode: z.string().min(1).optional(),
  divisionCode: z.string().min(1).optional(),
  branchCode: z.string().min(1).optional(),
  grossAmount: VdmGrossAmountSchema.optional(),
  calculateTax: z.boolean().optional(),
  shippingProvince: z.string().min(1).optional(),
  emailToAddress: z.string().email().optional().or(z.literal("")),
  emailCcAddress: z.string().max(70).optional().or(z.literal("")),
  emailBccAddress: z.string().max(70).optional().or(z.literal("")),
  specialInstructions1: z.string().max(30).optional(),
  specialInstructions2: z.string().max(30).optional(),
  specialInstructions3: z.string().max(30).optional(),
  commentLine1: z.string().optional(),
  commentLine2: z.string().optional(),
});

/**
 * Update VDM response schema
 */
export const UpdateVDMResponseSchema = VDMSchema;

/**
 * Send VDM input schema
 */
export const SendVDMInputSchema = z.object({
  id: UUIDSchema,
});

/**
 * Send VDM response schema
 */
export const SendVDMResponseSchema = VDMSchema;

/**
 * Refresh AS400 input schema
 */
export const RefreshAS400InputSchema = z.object({
  id: UUIDSchema,
});

/**
 * Refresh AS400 response schema
 */
export const RefreshAS400ResponseSchema = z.discriminatedUnion("found", [
  z.object({ found: z.literal(true), vdm: VDMSchema, message: z.string() }),
  z.object({ found: z.literal(false), message: z.string() }),
]);

/**
 * Refresh All AS400 response schema — bulk refresh for all pending VDMs
 */
export const RefreshAllAS400ResponseSchema = z.object({
  updatedCount: z.int().nonnegative(),
  checkedCount: z.int().nonnegative(),
  message: z.string(),
});

/**
 * Delete VDM input schema
 */
export const DeleteVDMInputSchema = z.object({
  id: UUIDSchema,
});

/**
 * Delete VDM response schema
 */
export const DeleteVDMResponseSchema = z.object({
  success: z.boolean(),
});

/**
 * VDM supplier company (from co_companies with S&D module enabled)
 */
export const VDMSupplierCompanySchema = z.object({
  id: UUIDSchema,
  name: z.string(),
  vendorNumber: z.string(),
  vdmCcEmail: z.string().nullable(),
});

export const ListVDMCompaniesResponseSchema = z.object({
  companies: z.array(VDMSupplierCompanySchema),
});

/**
 * Company hierarchy from sd_company_locations table
 */
export const CompanyHierarchyBranchSchema = z.object({
  code: z.string(),
  description: z.string(),
});

export const CompanyHierarchyDivisionSchema = z.object({
  code: z.string(),
  name: z.string().nullable().optional(),
  branches: z.array(CompanyHierarchyBranchSchema),
});

export const CompanyHierarchyCompanySchema = z.object({
  code: z.string(),
  name: z.string().nullable().optional(),
  divisions: z.array(CompanyHierarchyDivisionSchema),
});

export const GetCompanyHierarchyResponseSchema = z.object({
  companies: z.array(CompanyHierarchyCompanySchema),
});

/**
 * Linked 844 submissions for a VDM
 */
export const GetVDMLinkedSubmissionsInputSchema = z.object({
  vdmId: UUIDSchema,
});

const LinkedSubmissionSummarySchema = z.object({
  id: UUIDSchema,
  submissionNumber: z.int(),
  companyCode: z.string(),
  divisionCode: z.string(),
  sellingLocationCode: z.string(),
  deliveryMethod: z.string(),
  totalClaimedAmount: DecimalStringSchema,
  status: z.string(),
  externalReference: z.string().nullable().optional(),
  lineCount: z.int().nonnegative().optional(),
  sentAt: z.coerce.date().nullable().optional(),
  company: z.object({ id: UUIDSchema, name: z.string() }).nullable().optional(),
});

export const GetVDMLinkedSubmissionsResponseSchema = z.object({
  submissions: z.array(LinkedSubmissionSummarySchema),
});

export const LinkSubmissionToVDMFromVDMInputSchema = z.object({
  vdmId: UUIDSchema,
  submissionId: UUIDSchema,
});

export const LinkSubmissionToVDMFromVDMResponseSchema = z.object({
  success: z.boolean(),
});

export const UnlinkSubmissionFromVDMFromVDMInputSchema = z.object({
  vdmId: UUIDSchema,
  submissionId: UUIDSchema,
});

export const UnlinkSubmissionFromVDMFromVDMResponseSchema = z.object({
  success: z.boolean(),
});

/**
 * Per-line linking: attach a VDM to specific 844 transactions (sd_e844d ids).
 * Used by the 844 submission detail modal to link/unlink on a per-line basis.
 * The backend validates that every selected transaction is in the VDM's tax
 * province before linking.
 */
export const LinkTransactionsToVDMInputSchema = z.object({
  vdmId: UUIDSchema,
  transactionIds: z.array(UUIDSchema).min(1),
});

export const LinkTransactionsToVDMResponseSchema = z.object({
  linkedCount: z.int(),
});

export const UnlinkTransactionsFromVDMInputSchema = z.object({
  transactionIds: z.array(UUIDSchema).min(1),
  // When provided, only unlink rows currently pointing at this VDM — guards
  // against a stale modal unlinking a link another user just changed.
  vdmId: UUIDSchema.optional(),
});

export const UnlinkTransactionsFromVDMResponseSchema = z.object({
  unlinkedCount: z.int(),
});

/**
 * VDM RPC Contracts
 */
export const vdmContracts = {
  listCompanies: oc.output(ListVDMCompaniesResponseSchema).route({
    summary: "List VDM supplier companies",
    description:
      "Lists ship-and-debit supplier companies that have a linked supplier, with vendor number and default CC email, for choosing a vendor on a new vendor debit memo.",
    method: "GET",
  }),
  getCompanyHierarchy: oc.output(GetCompanyHierarchyResponseSchema).route({
    summary: "Get company, division, and branch hierarchy",
    description:
      "Returns the configured company → division → branch codes and names used to pick where a vendor debit memo (VDM) is booked.",
    method: "GET",
  }),
  list: oc.input(ListVDMsInputSchema).output(ListVDMsResponseSchema).route({
    summary: "List vendor debit memos",
    description:
      "Lists ship-and-debit vendor debit memos (VDMs) with pagination, filtered by status, supplier, company, division, VDM number, search text, or created date, including linked 844 submission counts.",
    method: "GET",
  }),
  downloadExcel: oc
    .input(DownloadVDMsExcelInputSchema)
    .output(DownloadVDMsExcelResponseSchema)
    .route({
      summary: "Export vendor debit memos to Excel",
      description:
        "Builds an Excel workbook of every VDM matching the list filters, one row per VDM, returned as base64; flags when results exceed the export row cap.",
      method: "POST",
    }),
  get: oc.input(GetVDMInputSchema).output(GetVDMResponseSchema).route({
    summary: "Get a vendor debit memo",
    description:
      "Returns one vendor debit memo (VDM) by id with amounts, taxes, codes, email addresses, status, error, and supplier.",
    method: "GET",
  }),
  create: oc.input(CreateVDMInputSchema).output(CreateVDMResponseSchema).route({
    summary: "Create a vendor debit memo",
    description:
      "Creates a vendor debit memo for a supplier, company, division, and branch with gross amount and tax settings, and uploads a create request to the ERP for processing.",
    method: "POST",
  }),
  update: oc.input(UpdateVDMInputSchema).output(UpdateVDMResponseSchema).route({
    summary: "Update vendor debit memo fields",
    description:
      "Saves edits to a VDM already confirmed by the ERP (amount, codes, emails, instructions) and logs the changes to its activity; does not send the edit to the ERP.",
    method: "POST",
  }),
  send: oc.input(SendVDMInputSchema).output(SendVDMResponseSchema).route({
    summary: "Send vendor debit memo edit to the ERP",
    description:
      "Uploads an edit request for a confirmed VDM to the ERP and moves it to pending ERP edit status.",
    method: "POST",
  }),
  retryCreation: oc
    .input(UpdateVDMInputSchema)
    .output(SendVDMResponseSchema)
    .route({
      summary: "Retry a failed vendor debit memo creation",
      description:
        "For a VDM whose ERP creation failed, applies corrected amount, branch, province, tax, instruction, or CC/BCC fields and re-uploads the create request to the ERP.",
      method: "POST",
    }),
  delete: oc.input(DeleteVDMInputSchema).output(DeleteVDMResponseSchema).route({
    summary: "Delete a vendor debit memo",
    description:
      "Deletes a vendor debit memo (VDM) record by id within the organization.",
    method: "POST",
  }),
  refreshAS400: oc
    .input(RefreshAS400InputSchema)
    .output(RefreshAS400ResponseSchema)
    .route({
      summary: "Check ERP response for a vendor debit memo",
      description:
        "Reads the ERP's response file for one pending or failed VDM and applies the result, setting the ERP VDM number, confirmed status, or error.",
      method: "POST",
    }),
  refreshAllAS400: oc.output(RefreshAllAS400ResponseSchema).route({
    summary: "Refresh all pending VDMs from the ERP",
    description:
      "Checks the ERP response files for every vendor debit memo awaiting create or edit confirmation and updates their status; returns checked and updated counts.",
    method: "POST",
  }),
  listActivities: oc
    .input(ListVDMActivitiesInputSchema)
    .output(ListVDMActivitiesResponseSchema)
    .route({
      summary: "List vendor debit memo activity",
      description:
        "Returns the paginated activity timeline for one VDM: comments, creation, ERP confirmations, errors, edits, and linking events, with the acting user.",
      method: "GET",
    }),
  addComment: oc
    .input(AddVDMCommentInputSchema)
    .output(AddVDMCommentResponseSchema)
    .route({
      summary: "Add a comment to a vendor debit memo",
      description:
        "Posts a user comment to a VDM's activity timeline and returns the new activity entry.",
      method: "POST",
    }),
  createBatch: oc
    .input(CreateBatchVDMsInputSchema)
    .output(CreateBatchVDMsResponseSchema)
    .route({
      summary: "Create vendor debit memos in batch",
      description:
        "Creates up to 100 vendor debit memos at once, uploads each to the ERP, and links any given 844 transactions; returns created and failed entries.",
      method: "POST",
    }),
  getLinkedSubmissions: oc
    .input(GetVDMLinkedSubmissionsInputSchema)
    .output(GetVDMLinkedSubmissionsResponseSchema)
    .route({
      summary: "List 844 submissions linked to a VDM",
      description:
        "Returns the 844 ship-and-debit claim submissions linked to a vendor debit memo, with codes, claimed totals, status, line counts, and sent dates.",
      method: "GET",
    }),
  linkSubmission: oc
    .input(LinkSubmissionToVDMFromVDMInputSchema)
    .output(LinkSubmissionToVDMFromVDMResponseSchema)
    .route({
      summary: "Link an 844 submission to a VDM",
      description:
        "Attaches an 844 claim submission and its transactions to a vendor debit memo and records the link in activity.",
      method: "POST",
    }),
  unlinkSubmission: oc
    .input(UnlinkSubmissionFromVDMFromVDMInputSchema)
    .output(UnlinkSubmissionFromVDMFromVDMResponseSchema)
    .route({
      summary: "Unlink an 844 submission from a VDM",
      description:
        "Removes the link between an 844 claim submission and a vendor debit memo, clearing its transaction links, and records the change in activity.",
      method: "POST",
    }),
  linkTransactions: oc
    .input(LinkTransactionsToVDMInputSchema)
    .output(LinkTransactionsToVDMResponseSchema)
    .route({
      summary: "Link 844 claim lines to a VDM",
      description:
        "Attaches specific 844 claim transactions to a vendor debit memo; every line must be in the VDM's tax province. Returns the linked count.",
      method: "POST",
    }),
  unlinkTransactions: oc
    .input(UnlinkTransactionsFromVDMInputSchema)
    .output(UnlinkTransactionsFromVDMResponseSchema)
    .route({
      summary: "Unlink 844 claim lines from a VDM",
      description:
        "Detaches the given 844 claim transactions from their vendor debit memo, optionally only when still linked to a given VDM. Returns the unlinked count.",
      method: "POST",
    }),
} as const;

/**
 * Company / Division / Branch hierarchy for VDM creation.
 * A branch belongs to exactly one division; a division belongs to exactly one company.
 */
export interface VDMBranch {
  code: string;
}

export interface VDMDivision {
  code: string;
  branches: VDMBranch[];
}

export interface VDMCompany {
  code: string;
  divisions: VDMDivision[];
}

/** @deprecated Use the `getCompanyHierarchy` RPC endpoint instead, which loads from `sd_company_locations` table. */
export const VDM_COMPANY_HIERARCHY: VDMCompany[] = [
  {
    code: "W",
    divisions: [
      { code: "+", branches: [] },
      { code: "A", branches: [{ code: "1" }, { code: "2" }] },
      { code: "B", branches: [{ code: "3" }, { code: "4" }] },
      { code: "C", branches: [{ code: "5" }, { code: "6" }] },
      { code: "D", branches: [{ code: "7" }, { code: "10" }] },
      { code: "E", branches: [{ code: "11" }, { code: "12" }] },
      { code: "F", branches: [{ code: "13" }, { code: "14" }] },
      { code: "G", branches: [{ code: "15" }, { code: "16" }] },
      { code: "H", branches: [{ code: "17" }, { code: "18" }] },
      { code: "J", branches: [] },
      { code: "R", branches: [] },
      { code: "T", branches: [{ code: "8" }, { code: "9" }] },
    ],
  },
  {
    code: "C",
    divisions: [{ code: "J", branches: [{ code: "K" }] }],
  },
  {
    code: "Q",
    divisions: [
      { code: "E", branches: [] },
      { code: "O", branches: [] },
    ],
  },
  {
    code: "R",
    divisions: [
      { code: "A", branches: [] },
      { code: "H", branches: [] },
    ],
  },
];

// Type exports
export type VDMStatus = z.infer<typeof VDMStatusSchema>;
export type VDMSortBy = z.infer<typeof VDMSortBySchema>;
export type VDM = z.infer<typeof VDMSchema>;
export type ListVDMsInput = z.infer<typeof ListVDMsInputSchema>;
export type ListVDMsResponse = z.infer<typeof ListVDMsResponseSchema>;
export type VDMListFilters = z.infer<typeof VDMListFiltersSchema>;
export type DownloadVDMsExcelResponse = z.infer<
  typeof DownloadVDMsExcelResponseSchema
>;
export type DownloadVDMsExcelInput = z.infer<
  typeof DownloadVDMsExcelInputSchema
>;
export type GetVDMInput = z.infer<typeof GetVDMInputSchema>;
export type GetVDMResponse = z.infer<typeof GetVDMResponseSchema>;
export type VDMActivityType = z.infer<typeof VDMActivityTypeSchema>;
export type VDMActivity = z.infer<typeof VDMActivitySchema>;
export type ListVDMActivitiesInput = z.infer<
  typeof ListVDMActivitiesInputSchema
>;
export type ListVDMActivitiesResponse = z.infer<
  typeof ListVDMActivitiesResponseSchema
>;
export type AddVDMCommentInput = z.infer<typeof AddVDMCommentInputSchema>;
export type AddVDMCommentResponse = z.infer<typeof AddVDMCommentResponseSchema>;
export type CreateVDMInput = z.infer<typeof CreateVDMInputSchema>;
export type CreateVDMResponse = z.infer<typeof CreateVDMResponseSchema>;
export type CreateBatchVDMsInput = z.infer<typeof CreateBatchVDMsInputSchema>;
export type CreateBatchVDMsResponse = z.infer<
  typeof CreateBatchVDMsResponseSchema
>;
export type UpdateVDMInput = z.infer<typeof UpdateVDMInputSchema>;
export type UpdateVDMResponse = z.infer<typeof UpdateVDMResponseSchema>;
export type SendVDMInput = z.infer<typeof SendVDMInputSchema>;
export type SendVDMResponse = z.infer<typeof SendVDMResponseSchema>;
export type RefreshAS400Input = z.infer<typeof RefreshAS400InputSchema>;
export type RefreshAS400Response = z.infer<typeof RefreshAS400ResponseSchema>;
export type RefreshAllAS400Response = z.infer<
  typeof RefreshAllAS400ResponseSchema
>;
export type DeleteVDMInput = z.infer<typeof DeleteVDMInputSchema>;
export type DeleteVDMResponse = z.infer<typeof DeleteVDMResponseSchema>;
export type VDMSupplierCompany = z.infer<typeof VDMSupplierCompanySchema>;
export type ListVDMCompaniesResponse = z.infer<
  typeof ListVDMCompaniesResponseSchema
>;
export type CompanyHierarchyBranch = z.infer<
  typeof CompanyHierarchyBranchSchema
>;
export type CompanyHierarchyDivision = z.infer<
  typeof CompanyHierarchyDivisionSchema
>;
export type CompanyHierarchyCompany = z.infer<
  typeof CompanyHierarchyCompanySchema
>;
export type GetCompanyHierarchyResponse = z.infer<
  typeof GetCompanyHierarchyResponseSchema
>;
